import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Trash2, Pencil, Calculator, FileText, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (Number(n) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EMPTY_RIGA = { codice_tributo: '', descrizione: '', anno_riferimento: new Date().getFullYear(), periodo_riferimento: '', importo_debito: 0, importo_credito: 0, codice_ente: '', codice_sede: '', causale_contributo: '' };
const EMPTY_F24 = () => ({
  numero_f24: '', tipo: 'ordinario',
  contribuente_nome: '', contribuente_cf: '',
  data: new Date().toISOString().slice(0, 10), scadenza: new Date().toISOString().slice(0, 10),
  sezione_erario: [], sezione_inps: [], sezione_regioni: [], sezione_imu_enti: [], sezione_inail: [],
  stato: 'da_pagare', modalita_pagamento: '', ravvedimento: false, ravvedimento_giorni_ritardo: 0, note: '',
});

const SEZIONI = [
  { key: 'sezione_erario', label: 'Erario', codeKey: 'erario' },
  { key: 'sezione_inps', label: 'INPS', codeKey: 'inps' },
  { key: 'sezione_regioni', label: 'Regioni', codeKey: 'regioni' },
  { key: 'sezione_imu_enti', label: 'IMU / Enti locali', codeKey: 'imu_enti_locali' },
  { key: 'sezione_inail', label: 'INAIL', codeKey: 'inail' },
];

export default function F24Page() {
  const [f24List, setF24List] = useState([]);
  const [codiciTributo, setCodiciTributo] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroAnno, setFiltroAnno] = useState(String(new Date().getFullYear()));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_F24());
  const [ravvedimentoImporto, setRavvedimentoImporto] = useState(0);
  const [ravvedimentoResult, setRavvedimentoResult] = useState(null);

  // Auto-compilazione
  const [autoPeriodo, setAutoPeriodo] = useState({
    tipo: 'mensile',
    anno: new Date().getFullYear(),
    mese: new Date().getMonth() + 1,
    trimestre: Math.ceil((new Date().getMonth() + 1) / 3),
  });
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoPreview, setAutoPreview] = useState(null);

  useEffect(() => { init(); }, []);
  useEffect(() => { fetchF24(); }, [filtroAnno]);

  const init = async () => {
    try {
      const [rc, rl] = await Promise.all([
        axios.get(`${API}/f24/codici-tributo`, { withCredentials: true }),
        axios.get(`${API}/f24?anno=${filtroAnno}`, { withCredentials: true }),
      ]);
      setCodiciTributo(rc.data || {});
      setF24List(rl.data || []);
    } catch { toast.error('Errore caricamento F24'); }
    finally { setLoading(false); }
  };

  const fetchF24 = async () => {
    try {
      const r = await axios.get(`${API}/f24?anno=${filtroAnno}`, { withCredentials: true });
      setF24List(r.data || []);
    } catch { /* silent */ }
  };

  const openNew = () => { setEditing(null); setForm(EMPTY_F24()); setAutoPreview(null); setDialogOpen(true); };
  const openEdit = (f24) => { setEditing(f24); setForm({ ...EMPTY_F24(), ...f24 }); setAutoPreview(null); setDialogOpen(true); };

  const precompilaAuto = async () => {
    setAutoLoading(true);
    try {
      const params = new URLSearchParams({
        anno: String(autoPeriodo.anno),
        tipo_periodo: autoPeriodo.tipo,
      });
      if (autoPeriodo.tipo === 'mensile') params.append('mese', String(autoPeriodo.mese));
      else params.append('trimestre', String(autoPeriodo.trimestre));
      const r = await axios.get(`${API}/f24/_precompila/genera?${params.toString()}`, { withCredentials: true });
      setAutoPreview(r.data);
      // Applica al form
      setForm(f => ({
        ...f,
        scadenza: r.data.scadenza_suggerita || f.scadenza,
        sezione_erario: r.data.sezione_erario || [],
        sezione_inps: r.data.sezione_inps || [],
        sezione_regioni: r.data.sezione_regioni || [],
        sezione_imu_enti: r.data.sezione_imu_enti || [],
        sezione_inail: r.data.sezione_inail || [],
        note: r.data.note || f.note,
      }));
      const totale = (r.data.sezione_erario || []).length + (r.data.sezione_inps || []).length;
      if (totale === 0) {
        toast.info('Nessun dato da precompilare per il periodo selezionato (fatture, buste paga o compensi soci)');
      } else {
        toast.success(`Precompilato ${totale} righe · scadenza ${r.data.scadenza_suggerita}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore precompilazione');
    } finally { setAutoLoading(false); }
  };

  const addRiga = (sezKey) => setForm(f => ({ ...f, [sezKey]: [...(f[sezKey] || []), { ...EMPTY_RIGA }] }));
  const updateRiga = (sezKey, idx, patch) => setForm(f => ({ ...f, [sezKey]: f[sezKey].map((r, i) => i === idx ? { ...r, ...patch } : r) }));
  const removeRiga = (sezKey, idx) => setForm(f => ({ ...f, [sezKey]: f[sezKey].filter((_, i) => i !== idx) }));

  const totali = useMemo(() => {
    let d = 0, c = 0;
    for (const s of SEZIONI) for (const r of (form[s.key] || [])) { d += Number(r.importo_debito) || 0; c += Number(r.importo_credito) || 0; }
    return { debito: d, credito: c, saldo: d - c };
  }, [form]);

  const save = async () => {
    if (!form.contribuente_nome.trim() || !form.contribuente_cf.trim()) { toast.error('Contribuente e CF sono obbligatori'); return; }
    if (!form.data || !form.scadenza) { toast.error('Data versamento e scadenza obbligatorie'); return; }
    try {
      if (editing) await axios.put(`${API}/f24/${editing.id}`, form, { withCredentials: true });
      else await axios.post(`${API}/f24`, form, { withCredentials: true });
      toast.success(editing ? 'F24 aggiornato' : 'F24 creato');
      setDialogOpen(false);
      fetchF24();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore salvataggio F24'); }
  };
  const del = async (f24) => {
    if (!window.confirm(`Eliminare F24 ${f24.numero_f24}?`)) return;
    try { await axios.delete(`${API}/f24/${f24.id}`, { withCredentials: true }); toast.success('F24 eliminato'); fetchF24(); }
    catch { toast.error('Errore'); }
  };
  const calcolaRavvedimento = async () => {
    if (!form.ravvedimento_giorni_ritardo || !ravvedimentoImporto) return;
    try {
      const r = await axios.post(`${API}/f24/ravvedimento-calcolo?giorni_ritardo=${form.ravvedimento_giorni_ritardo}&importo=${ravvedimentoImporto}`, {}, { withCredentials: true });
      setRavvedimentoResult(r.data);
    } catch { toast.error('Errore calcolo'); }
  };

  const totalsList = useMemo(() => ({
    tot: f24List.reduce((s, x) => s + (Number(x.saldo_finale) || 0), 0),
    pagati: f24List.filter(x => x.stato === 'pagato').reduce((s, x) => s + (Number(x.saldo_finale) || 0), 0),
    daPagare: f24List.filter(x => x.stato === 'da_pagare').reduce((s, x) => s + (Number(x.saldo_finale) || 0), 0),
  }), [f24List]);

  const anni = useMemo(() => {
    const curr = new Date().getFullYear();
    return [curr, curr - 1, curr - 2, curr - 3];
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;

  return (
    <div className="space-y-6" data-testid="f24-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Modello F24</h1>
          <p className="text-slate-500 mt-1">Registrazione versamenti F24 con codici tributo aggiornati e ravvedimento operoso</p>
        </div>
        <div className="flex gap-2">
          <Select value={filtroAnno} onValueChange={setFiltroAnno}>
            <SelectTrigger className="w-[120px]" data-testid="f24-filtro-anno"><SelectValue /></SelectTrigger>
            <SelectContent>{anni.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="f24-new-btn"><Plus className="h-4 w-4 mr-1.5" />Nuovo F24</Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-slate-200 bg-slate-50/50"><CardContent className="p-4"><p className="text-[10px] text-slate-600 uppercase font-medium">Totale F24</p><p className="text-2xl font-bold text-slate-700">{fmt(totalsList.tot)}</p></CardContent></Card>
        <Card className="border-emerald-200 bg-emerald-50/50"><CardContent className="p-4"><p className="text-[10px] text-emerald-600 uppercase font-medium">Versati</p><p className="text-2xl font-bold text-emerald-700">{fmt(totalsList.pagati)}</p></CardContent></Card>
        <Card className="border-amber-200 bg-amber-50/50"><CardContent className="p-4"><p className="text-[10px] text-amber-600 uppercase font-medium">Da Pagare</p><p className="text-2xl font-bold text-amber-700">{fmt(totalsList.daPagare)}</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>N.</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Scadenza</TableHead>
                <TableHead>Contribuente</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Debiti</TableHead>
                <TableHead className="text-right">Crediti</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {f24List.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-10">Nessun F24 registrato per {filtroAnno}. Clicca &quot;Nuovo F24&quot; per iniziare.</TableCell></TableRow>
              ) : f24List.map(f => (
                <TableRow key={f.id} data-testid={`f24-row-${f.id}`}>
                  <TableCell className="font-mono text-xs">{f.numero_f24}</TableCell>
                  <TableCell>{f.data}</TableCell>
                  <TableCell className={f.stato === 'da_pagare' && f.scadenza < new Date().toISOString().slice(0,10) ? 'text-red-600 font-medium' : ''}>{f.scadenza}</TableCell>
                  <TableCell className="max-w-[200px] truncate" title={f.contribuente_nome}>{f.contribuente_nome}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs capitalize">{f.tipo}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{fmt(f.totale_debiti)}</TableCell>
                  <TableCell className="text-right font-mono text-slate-500">{fmt(f.totale_crediti)}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{fmt(f.saldo_finale)}</TableCell>
                  <TableCell>
                    {f.stato === 'pagato'
                      ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Pagato</Badge>
                      : f.stato === 'annullato'
                      ? <Badge className="bg-slate-100 text-slate-600 border-slate-200" variant="outline">Annullato</Badge>
                      : <Badge className="bg-amber-100 text-amber-800 border-amber-200" variant="outline">Da Pagare</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(f)} data-testid={`edit-f24-${f.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => del(f)} data-testid={`delete-f24-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="f24-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifica F24' : 'Nuovo F24'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Intestazione */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div><Label>N. F24 (auto)</Label><Input value={form.numero_f24} onChange={e => setForm({...form, numero_f24: e.target.value})} placeholder="F24-2026-0001" /></div>
              <div><Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={v => setForm({...form, tipo: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ordinario">Ordinario</SelectItem>
                    <SelectItem value="elide">Elide (identificativi vari)</SelectItem>
                    <SelectItem value="accise">Accise</SelectItem>
                    <SelectItem value="semplificato">Semplificato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Data versamento</Label><Input type="date" value={form.data} onChange={e => setForm({...form, data: e.target.value})} /></div>
              <div><Label>Scadenza legale</Label><Input type="date" value={form.scadenza} onChange={e => setForm({...form, scadenza: e.target.value})} /></div>
              <div className="col-span-2"><Label>Contribuente *</Label><Input value={form.contribuente_nome} onChange={e => setForm({...form, contribuente_nome: e.target.value})} data-testid="f24-form-contribuente" /></div>
              <div><Label>C.F. / P.IVA *</Label><Input value={form.contribuente_cf} onChange={e => setForm({...form, contribuente_cf: e.target.value.toUpperCase()})} /></div>
              <div><Label>Modalità pagamento</Label><Input value={form.modalita_pagamento} onChange={e => setForm({...form, modalita_pagamento: e.target.value})} placeholder="Home banking, F24 online..." /></div>
            </div>

            {/* Auto-compilazione da dati sistema */}
            {!editing && (
              <Card className="border-indigo-200 bg-indigo-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Zap className="h-4 w-4 text-indigo-600" /> Compila automaticamente dai dati del gestionale
                  </CardTitle>
                  <p className="text-[11px] text-slate-600 mt-0.5">
                    Aggrega IVA dalle fatture, ritenute IRPEF/INPS dalle buste paga AI, ritenute d&apos;acconto dai compensi soci. INPS soci SNC (IVS artigiani/commercianti) da inserire manualmente se dovuto.
                  </p>
                </CardHeader>
                <CardContent className="p-3">
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                    <div>
                      <Label className="text-xs">Tipo periodo</Label>
                      <Select value={autoPeriodo.tipo} onValueChange={v => setAutoPeriodo(p => ({ ...p, tipo: v }))}>
                        <SelectTrigger data-testid="f24-auto-tipo"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mensile">Mensile</SelectItem>
                          <SelectItem value="trimestrale">Trimestrale</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Anno</Label>
                      <Input type="number" min={2000} max={2100} value={autoPeriodo.anno}
                        onChange={e => setAutoPeriodo(p => ({ ...p, anno: parseInt(e.target.value, 10) || new Date().getFullYear() }))}
                        data-testid="f24-auto-anno" />
                    </div>
                    {autoPeriodo.tipo === 'mensile' ? (
                      <div>
                        <Label className="text-xs">Mese</Label>
                        <Select value={String(autoPeriodo.mese)} onValueChange={v => setAutoPeriodo(p => ({ ...p, mese: parseInt(v, 10) }))}>
                          <SelectTrigger data-testid="f24-auto-mese"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                              <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : (
                      <div>
                        <Label className="text-xs">Trimestre</Label>
                        <Select value={String(autoPeriodo.trimestre)} onValueChange={v => setAutoPeriodo(p => ({ ...p, trimestre: parseInt(v, 10) }))}>
                          <SelectTrigger data-testid="f24-auto-trim"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">I trim (gen-mar)</SelectItem>
                            <SelectItem value="2">II trim (apr-giu)</SelectItem>
                            <SelectItem value="3">III trim (lug-set)</SelectItem>
                            <SelectItem value="4">IV trim (ott-dic)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div className="md:col-span-2">
                      <Button onClick={precompilaAuto} disabled={autoLoading} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="f24-auto-precompila">
                        <Zap className="h-4 w-4 mr-1.5" />
                        {autoLoading ? 'Aggregazione…' : 'Compila automatico'}
                      </Button>
                    </div>
                  </div>
                  {autoPreview && (
                    <div className="mt-3 text-xs bg-white p-2 rounded border border-indigo-200 space-y-0.5">
                      <div><strong>IVA</strong>: debito € {fmt(autoPreview.dettagli_aggregazione.iva.iva_debito)} · credito € {fmt(autoPreview.dettagli_aggregazione.iva.iva_credito)} · <strong>saldo € {fmt(autoPreview.dettagli_aggregazione.iva.saldo)}</strong></div>
                      <div><strong>Buste paga</strong>: {autoPreview.dettagli_aggregazione.buste_paga.n_buste} · IRPEF € {fmt(autoPreview.dettagli_aggregazione.buste_paga.irpef)} · INPS € {fmt(autoPreview.dettagli_aggregazione.buste_paga.inps_dip + autoPreview.dettagli_aggregazione.buste_paga.inps_az)}</div>
                      <div><strong>Compensi soci</strong>: {autoPreview.dettagli_aggregazione.compensi_soci.n_compensi} · Ritenuta € {fmt(autoPreview.dettagli_aggregazione.compensi_soci.ritenuta_acconto)}</div>
                      <div className="text-indigo-700 pt-1"><strong>Scadenza suggerita:</strong> {autoPreview.scadenza_suggerita}</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Tabs defaultValue="sezione_erario">
              <TabsList>
                {SEZIONI.map(s => <TabsTrigger key={s.key} value={s.key}>{s.label} {(form[s.key] || []).length > 0 && <Badge className="ml-1.5 h-4 min-w-4 px-1 text-[10px]">{(form[s.key] || []).length}</Badge>}</TabsTrigger>)}
              </TabsList>
              {SEZIONI.map(s => (
                <TabsContent key={s.key} value={s.key}>
                  <div className="flex justify-end mb-2"><Button size="sm" variant="outline" onClick={() => addRiga(s.key)} data-testid={`f24-add-riga-${s.key}`}><Plus className="h-3 w-3 mr-1" />Aggiungi riga</Button></div>
                  {(form[s.key] || []).map((r, idx) => (
                    <Card key={idx} className="mb-2 border-slate-200"><CardContent className="p-3">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                        <div><Label className="text-xs">Codice tributo</Label>
                          <Select value={r.codice_tributo || '__custom__'} onValueChange={v => {
                            if (v === '__custom__') return updateRiga(s.key, idx, { codice_tributo: '' });
                            const item = (codiciTributo[s.codeKey] || []).find(x => x.codice === v);
                            updateRiga(s.key, idx, { codice_tributo: v, descrizione: item?.descrizione || r.descrizione });
                          }}>
                            <SelectTrigger data-testid={`f24-riga-cod-${s.key}-${idx}`}><SelectValue placeholder="Seleziona" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__custom__">— Custom —</SelectItem>
                              {(codiciTributo[s.codeKey] || []).map(c => <SelectItem key={c.codice} value={c.codice}>{c.codice} · {c.descrizione}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {!r.codice_tributo && <Input className="mt-1" placeholder="Codice manuale" value={r.codice_tributo} onChange={e => updateRiga(s.key, idx, { codice_tributo: e.target.value })} />}
                        </div>
                        <div><Label className="text-xs">Anno rif.</Label><Input type="number" value={r.anno_riferimento} onChange={e => updateRiga(s.key, idx, { anno_riferimento: parseInt(e.target.value, 10) || new Date().getFullYear() })} /></div>
                        <div><Label className="text-xs">Periodo (es. 07)</Label><Input value={r.periodo_riferimento} onChange={e => updateRiga(s.key, idx, { periodo_riferimento: e.target.value })} /></div>
                        <div className="flex items-end gap-1">
                          <div className="flex-1"><Label className="text-xs">Debito €</Label><Input type="number" step="0.01" value={r.importo_debito} onChange={e => updateRiga(s.key, idx, { importo_debito: parseFloat(e.target.value) || 0 })} /></div>
                          <Button size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-500" onClick={() => removeRiga(s.key, idx)}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                        <div className="col-span-3"><Label className="text-xs">Descrizione</Label><Input value={r.descrizione} onChange={e => updateRiga(s.key, idx, { descrizione: e.target.value })} /></div>
                        <div><Label className="text-xs">Credito €</Label><Input type="number" step="0.01" value={r.importo_credito} onChange={e => updateRiga(s.key, idx, { importo_credito: parseFloat(e.target.value) || 0 })} /></div>
                      </div>
                    </CardContent></Card>
                  ))}
                  {(form[s.key] || []).length === 0 && <p className="text-sm text-slate-400 text-center py-4">Nessuna riga in {s.label}. Aggiungi la prima con il pulsante sopra.</p>}
                </TabsContent>
              ))}
            </Tabs>

            {/* Ravvedimento */}
            <Card className="border-amber-200 bg-amber-50/30">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" /> Ravvedimento operoso</CardTitle></CardHeader>
              <CardContent className="p-3">
                <div className="grid grid-cols-3 gap-2 items-end">
                  <div><Label className="text-xs">Giorni ritardo</Label><Input type="number" value={form.ravvedimento_giorni_ritardo} onChange={e => setForm({...form, ravvedimento_giorni_ritardo: parseInt(e.target.value, 10) || 0, ravvedimento: parseInt(e.target.value, 10) > 0})} /></div>
                  <div><Label className="text-xs">Importo di riferimento €</Label><Input type="number" step="0.01" value={ravvedimentoImporto} onChange={e => setRavvedimentoImporto(parseFloat(e.target.value) || 0)} /></div>
                  <Button size="sm" onClick={calcolaRavvedimento} disabled={!form.ravvedimento_giorni_ritardo || !ravvedimentoImporto} data-testid="calcola-ravvedimento"><Calculator className="h-3.5 w-3.5 mr-1.5" />Calcola</Button>
                </div>
                {ravvedimentoResult && (
                  <div className="mt-2 text-xs bg-white p-2 rounded border border-amber-200">
                    <div>Sanzione: <strong>€ {fmt(ravvedimentoResult.sanzione)}</strong> ({(ravvedimentoResult.sanzione_pct * 100).toFixed(3)}%)</div>
                    <div>Interessi legali (5% annuo): <strong>€ {fmt(ravvedimentoResult.interessi)}</strong></div>
                    <div className="text-amber-800 mt-1">Totale ravvedimento: <strong>€ {fmt(ravvedimentoResult.totale)}</strong></div>
                    <div className="text-slate-500 mt-1 italic">Aggiungi manualmente 2 righe in Sezione Erario: codice <code>1668</code> (interessi) e <code>8904</code> (sanzione).</div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stato + note */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Stato</Label>
                <Select value={form.stato} onValueChange={v => setForm({...form, stato: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="da_pagare">Da pagare</SelectItem>
                    <SelectItem value="pagato">Pagato</SelectItem>
                    <SelectItem value="annullato">Annullato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">Totale debiti: € {fmt(totali.debito)}</div>
                <div className="text-xs text-slate-500">Totale crediti: € {fmt(totali.credito)}</div>
                <div className="text-lg font-bold">Saldo: € {fmt(totali.saldo)}</div>
              </div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={form.note} onChange={e => setForm({...form, note: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="f24-form-save">{editing ? 'Salva modifiche' : 'Crea F24'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
